import React, { useState, useEffect } from "react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import StripePaymentWrapper from "../components/StripePaymentWrapper";
import PayPalPayment from "../components/PayPalPayment";
import CountrySelect from "../components/CountrySelect";

export default function Cart() {
  const { cart, removeFromCart, updateQuantity, clearCart, getCartItemCount } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState("cart"); // cart | delivery | payment
  const [shippingCost, setShippingCost] = useState(0);
  const [finalTotal, setFinalTotal] = useState(0);

  const [shipping, setShipping] = useState({
    name: "",
    phone: "",
    address: "",
    city: "",
    postalCode: "",
    country: ""
  });

  const items = cart.items || [];
  const europeanCountries = ['AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK'];

  const convertToUSD = (mad) => (mad / 10).toFixed(2);

  // Calcul des frais de livraison et du total final
  useEffect(() => {
    const subtotalUSD = parseFloat(convertToUSD(cart.total));
    
    if (step === "delivery" || step === "payment") {
      // Vérifier si le pays est européen
      const isEuropean = europeanCountries.includes(shipping.country);
      const shippingFee = isEuropean ? 4 : 10; // CHANGEMENT ICI: utilisation de shippingFee au lieu de shipping
      setShippingCost(shippingFee);
      setFinalTotal(subtotalUSD + shippingFee);
    } else {
      setShippingCost(0);
      setFinalTotal(subtotalUSD);
    }
  }, [shipping.country, cart.total, step, europeanCountries]);

  // ========================= Checkout =========================
  const checkout = () => {
    if (!user) {
      navigate("/login", { state: { from: "/cart" } });
      return;
    }
    setStep("delivery");
  };

  const submitDelivery = (e) => {
    e.preventDefault();
    setStep("payment");
  };

  const onPaySuccess = (paymentData) => {
    navigate("/payment-success", {
      state: {
        paymentId: paymentData.paymentId,
        orderId: paymentData.orderId,
        amount: paymentData.amount,
        shippingCost: shippingCost,
        finalTotal: finalTotal
      },
    });
  };

  const onPayError = (e) => alert("Payment error: " + e);

  return (
    <div className="min-h-screen bg-[#fffaf3] py-12">
      <div className="max-w-4xl mx-auto px-6">
        <div className="bg-white rounded-lg shadow-lg p-8">

          {/* ========================= HEADER ========================= */}
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-serif font-light text-[#2f2a1e]">
              My Cart {user && `(${user.name})`}
            </h1>

            <div className="text-right">
              <p className="text-[#7a7250]">
                {getCartItemCount()} item{getCartItemCount() > 1 ? "s" : ""}
              </p>

              {user ? (
                <p className="text-sm text-green-600">✅ Personal cart</p>
              ) : (
                <p className="text-sm text-orange-600">
                  ⚠️ Temporary cart — <Link to="/login" className="underline">Log in</Link>
                </p>
              )}
            </div>
          </div>

          {/* ========================= EMPTY CART ========================= */}
          {items.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#7a7250] text-lg mb-4">Your cart is empty</p>
              <Link
                to="/shop"
                className="bg-[#2f2a1e] text-white px-6 py-3 rounded-lg hover:bg-[#d0b125] transition"
              >
                Browse products
              </Link>
            </div>
          ) : step === "delivery" ? (
            /* ========================= DELIVERY FORM ========================= */
            <div>
              <button onClick={() => setStep("cart")} className="text-[#7a7250] mb-4">
                ← Back to cart
              </button>

              <h2 className="text-2xl font-serif font-light text-[#2f2a1e] mb-6">
                Delivery Information
              </h2>

              <form onSubmit={submitDelivery} className="space-y-4">
                <input
                  type="text"
                  placeholder="Full name"
                  value={shipping.name}
                  onChange={(e) => setShipping({ ...shipping, name: e.target.value })}
                  required
                  className="w-full p-3 border rounded"
                />
                <input
                  type="text"
                  placeholder="Phone"
                  value={shipping.phone}
                  onChange={(e) => setShipping({ ...shipping, phone: e.target.value })}
                  required
                  className="w-full p-3 border rounded"
                />
                <input
                  type="text"
                  placeholder="Address"
                  value={shipping.address}
                  onChange={(e) => setShipping({ ...shipping, address: e.target.value })}
                  required
                  className="w-full p-3 border rounded"
                />
                <input
                  type="text"
                  placeholder="City"
                  value={shipping.city}
                  onChange={(e) => setShipping({ ...shipping, city: e.target.value })}
                  required
                  className="w-full p-3 border rounded"
                />
                <input
                  type="text"
                  placeholder="Postal Code"
                  value={shipping.postalCode}
                  onChange={(e) => setShipping({ ...shipping, postalCode: e.target.value })}
                  required
                  className="w-full p-3 border rounded"
                />

                <CountrySelect
                  value={shipping.country}
                  onChange={(code) => setShipping({ ...shipping, country: code })}
                />

                {/* Résumé des frais */}
                <div className="mt-6 pt-4 border-t border-[#e8e2d1]">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-[#7a7250]">Subtotal</span>
                      <span className="font-medium">{convertToUSD(cart.total)} USD</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#7a7250]">Shipping</span>
                      <span className="font-medium">
                        {shipping.country ? (
                          shippingCost > 0 ? (
                            `${shippingCost} USD ${europeanCountries.includes(shipping.country) ? '(Europe)' : '(International)'}`
                          ) : (
                            "Select country"
                          )
                        ) : "Select country"}
                      </span>
                    </div>
                    <div className="flex justify-between text-lg font-medium pt-2 border-t">
                      <span>Total</span>
                      <span>{finalTotal.toFixed(2)} USD</span>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={!shipping.country}
                  className={`w-full py-3 rounded-lg ${shipping.country ? 'bg-[#2f2a1e] text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                >
                  Continue to payment
                </button>
              </form>
            </div>
          ) : step === "payment" ? (
            /* ========================= PAYMENT SECTION ========================= */
            <div>
              <div className="flex justify-between items-center mb-6">
                <button onClick={() => setStep("delivery")} className="text-[#7a7250]">
                  ← Back
                </button>
                <h2 className="text-2xl font-serif font-light text-[#2f2a1e]">Secure Payment</h2>
                <div className="w-20"></div>
              </div>

              {/* Résumé de la commande */}
              <div className="mb-8 p-4 bg-[#fffaf3] rounded-lg">
                <h3 className="font-medium text-[#2f2a1e] mb-3">Order Summary</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-[#7a7250]">Subtotal</span>
                    <span>{convertToUSD(cart.total)} USD</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#7a7250]">Shipping to {shipping.country}</span>
                    <span>{shippingCost} USD</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-[#e8e2d1] font-medium">
                    <span>Total</span>
                    <span>{finalTotal.toFixed(2)} USD</span>
                  </div>
                </div>
              </div>

              {/* STRIPE */}
              <StripePaymentWrapper
                cart={cart}
                user={user}
                shipping={shipping}
                shippingCost={shippingCost}
                finalTotal={finalTotal}
                onSuccess={onPaySuccess}
                onError={onPayError}
              />

              {/* PAYPAL */}
              <div className="mt-8">
                <PayPalPayment
                  amount={finalTotal}
                  onSuccess={(paypalData) => {
                    onPaySuccess({
                      paymentId: paypalData.id,
                      orderId: paypalData.purchase_units[0].payments.captures[0].id,
                      amount: finalTotal,
                    });
                  }}
                  onError={onPayError}
                />
              </div>
            </div>
          ) : (
            /* ========================= CART ITEMS ========================= */
            <>
              <div className="space-y-6">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between border-b border-[#e8e2d1] pb-6"
                  >
                    <div className="flex-1">
                      <h3 className="text-lg text-[#2f2a1e]">{item.name}</h3>
                      <p className="text-[#7a7250] mt-1">{convertToUSD(item.price)} USD</p>
                    </div>

                    <div className="flex items-center space-x-6">
                      <div className="flex items-center border border-[#e8e2d1] rounded">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                          className="px-3 py-1 text-[#7a7250]"
                        >
                          −
                        </button>
                        <span className="px-3 py-1 border-l border-r border-[#e8e2d1]">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="px-3 py-1 text-[#7a7250]"
                        >
                          +
                        </button>
                      </div>

                      <p className="font-medium min-w-20 text-right text-[#2f2a1e]">
                        {convertToUSD(item.price * item.quantity)} USD
                      </p>

                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-gray-400 hover:text-red-500 p-2"
                      >
                        ✖
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-[#e8e2d1]">
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between">
                    <span className="text-[#7a7250]">Subtotal</span>
                    <span className="text-lg">{convertToUSD(cart.total)} USD</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#7a7250]">Shipping estimate</span>
                    <span className="text-sm">Europe: 4 USD • Other: 10 USD</span>
                  </div>
                  <div className="flex justify-between pt-4 border-t">
                    <div>
                      <span className="text-2xl text-[#2f2a1e]">Total</span>
                      <p className="text-sm text-[#7a7250]">
                        {items.length} item{items.length > 1 ? "s" : ""}
                      </p>
                    </div>
                    <span className="text-2xl font-medium text-[#2f2a1e]">
                      {convertToUSD(cart.total)} USD
                      <span className="text-sm block text-[#7a7250]">+ shipping at checkout</span>
                    </span>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <button
                    onClick={clearCart}
                    className="flex-1 border border-[#2f2a1e] text-[#2f2a1e] py-3 rounded-lg"
                  >
                    Empty cart
                  </button>

                  <button
                    onClick={checkout}
                    className="flex-1 bg-[#2f2a1e] text-white py-3 rounded-lg"
                  >
                    Checkout now ({convertToUSD(cart.total)} USD)
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}